import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api, API } from "@/lib/api";

// Ein-/ausklappbare Bereiche der Auftragsdetailseite. Zustand je Benutzer + Auftrag im Backend
// (GET/PUT /api/auftraege/{id}/ui-state), optimistisch im UI, gespeichert mit Verzögerung.
const Ctx = createContext(null);
const SAVE_DELAY_MS = 600;
// Klicks auf diese Elemente in der Kopfzeile klappen nicht ein/aus
const INTERACTIVE = "button, a, input, select, textarea, label, [role=combobox], [role=checkbox], [role=switch], [role=menuitem], [data-no-toggle]";

const save = (orderId, sections) => api.put(`/auftraege/${orderId}/ui-state`, { sections }).catch(() => {});

export function useOrderUiState(orderId) {
  const [sections, setSections] = useState({});
  const [loaded, setLoaded] = useState(false);
  const dirty = useRef(false);
  const pending = useRef(null);

  useEffect(() => {
    let alive = true;
    dirty.current = false;
    setLoaded(false);
    setSections({});
    api.get(`/auftraege/${orderId}/ui-state`)
      .then((r) => alive && setSections(r.data?.sections || {}))
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
      if (pending.current) { save(pending.current.orderId, pending.current.sections); pending.current = null; }
    };
  }, [orderId]);

  useEffect(() => {
    if (!dirty.current) return undefined;
    pending.current = { orderId, sections };
    const t = setTimeout(() => { pending.current = null; save(orderId, sections); }, SAVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [sections, orderId]);

  // Tab schließen/neu laden: ausstehende Änderung trotzdem senden
  useEffect(() => {
    const onHide = () => {
      const p = pending.current;
      if (!p) return;
      pending.current = null;
      fetch(`${API}/auftraege/${p.orderId}/ui-state`, {
        method: "PUT", keepalive: true, body: JSON.stringify({ sections: p.sections }),
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      }).catch(() => {});
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  const setCollapsed = (keys, value) => {
    dirty.current = true;
    setSections((prev) => {
      const next = { ...prev };
      [].concat(keys).forEach((k) => { next[k] = value; });
      return next;
    });
  };
  return { sections, loaded, setCollapsed, toggle: (k) => setCollapsed(k, !sections[k]) };
}

export function SectionCollapseProvider({ ui, children }) {
  return <Ctx.Provider value={ui}>{children}</Ctx.Provider>;
}

// Für Bereiche außerhalb des Providers (z. B. Positions-Dateien) bleibt alles unverändert
export function useSection(k) {
  const c = useContext(Ctx);
  if (!c || !k) return { enabled: false, collapsed: false, headerProps: {} };
  const collapsed = !!c.sections[k];
  const onClick = (e) => {
    if (e.target.closest(INTERACTIVE)) return;
    if (window.getSelection?.()?.toString()) return; // Text markieren soll nicht umschalten
    c.toggle(k);
  };
  return { enabled: true, collapsed, toggle: () => c.toggle(k), headerProps: { onClick, "data-section-header": k, "data-testid": `section-header-${k}` } };
}

// Chevron-Schalter: echter Button → Tab, Enter/Leertaste, aria-expanded
export function SectionToggle({ k, label = "Bereich" }) {
  const s = useSection(k);
  if (!s.enabled) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); s.toggle(); }}
      aria-expanded={!s.collapsed}
      aria-controls={`section-body-${k}`}
      aria-label={`${label} ${s.collapsed ? "ausklappen" : "einklappen"}`}
      title={s.collapsed ? "Ausklappen" : "Einklappen"}
      className="-ml-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors"
      data-testid={`section-toggle-${k}`}
    >
      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${s.collapsed ? "-rotate-90" : ""}`} />
    </button>
  );
}

// Kopfzeile eines Bereichs: ganze Zeile klickbar (Buttons/Felder darin bleiben eigenständig), Chevron als Schalter.
// openClassName gilt nur ausgeklappt (z. B. Abstand/Trennlinie zum Inhalt).
export function SectionHead({ k, label, className = "", openClassName = "", children }) {
  const s = useSection(k);
  return (
    <div className={`${className} ${s.collapsed ? "" : openClassName} ${s.enabled ? "cursor-pointer" : ""}`} {...s.headerProps}>
      <SectionToggle k={k} label={label} />
      {children}
    </div>
  );
}

export function SectionSummary({ k, children }) {
  const s = useSection(k);
  if (!s.enabled || !s.collapsed || !children) return null;
  return <span className="text-sm text-slate-500 truncate" data-testid={`section-summary-${k}`}>{children}</span>;
}

// Inhalt bleibt gemountet (kein Neuladen, Verhalten unverändert); Animation über grid-template-rows
export function SectionBody({ k, part = "", className = "", children }) {
  const s = useSection(k);
  const [animating, setAnimating] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    setAnimating(true);
    const t = setTimeout(() => setAnimating(false), 350); // falls transitionend ausbleibt
    return () => clearTimeout(t);
  }, [s.collapsed]);
  if (!s.enabled) return className ? <div className={className}>{children}</div> : children;
  const hide = s.collapsed || animating;
  return (
    <div
      id={`section-body-${k}${part ? `-${part}` : ""}`}
      className={`grid transition-[grid-template-rows] duration-200 ease-out ${s.collapsed ? "grid-rows-[0fr] !mt-0" : "grid-rows-[1fr]"}`}
      onTransitionEnd={(e) => { if (e.target === e.currentTarget) setAnimating(false); }}
      inert={s.collapsed ? true : undefined}
      aria-hidden={s.collapsed || undefined}
      data-testid={`section-body-${k}${part ? `-${part}` : ""}`}
      data-collapsed={s.collapsed ? "true" : "false"}
    >
      <div className={`min-h-0 ${hide ? "overflow-hidden" : ""} ${className}`}>{children}</div>
    </div>
  );
}
